import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Checkbox, Button, IconButton, Alert, TextField, InputAdornment, LinearProgress, alpha,
} from '@mui/material';
import {
  Download, PlaylistAddCheck, CheckCircleOutline,
  Badge as BadgeIcon, Search, Clear,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../services/admissions/admissionApplicationService';
import { AdmissionCycle, AdmissionApplication } from '../../types/admissions';
import EmptyState from '../common/EmptyState';
import TableRowSkeletons from '../common/TableRowSkeletons';
import { useDebounce } from '../../hooks/useDebounce';
import dateTimeUtils from '../../utils/dateTimeUtils';
import CandidateDrawer from './CandidateDrawer';
import { usePdfExport, useSchoolBranding, fetchPhotoDataUrls } from '../../utils/pdf';
import { photoFilenameOf } from '../../utils/personPhoto';
import { AdmitCardBulkDocument } from './pdf';
import PdfRenderingDialog from '../common/PdfRenderingDialog';
import BusyIcon from '../common/BusyIcon';
import SearchableSelect from '../common/SearchableSelect';
import ApplicationActionsMenu from './ApplicationActionsMenu';
import { TabToolbar, TableCard, GroupLabel, tableHeadCellSx, clickableRowSx, RowActionsCell, ApplicantCell, useCycleClassOptions, applicantMatches } from './tabLayout';
import { fmtClassName } from '../../utils/pdf/format';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import ConfirmationDialog, { ConfirmFacts } from '../common/ConfirmationDialog';
import { pathwayFor } from '../../utils/admissionAssessment';

/**
 * AdmitCardsTab — the one-stop admit-card console. Only applications in
 * `AssessmentScheduled` are eligible to receive a roll number; this tab
 * shows them split into "Ready to issue" vs "Issued". Rows open the candidate
 * drawer; the shared ⋮ menu carries per-application actions (including the
 * single admit-card download). Bulk issue / bulk download live in the toolbar.
 *
 * The server generates roll numbers from the cycle's `admitCardNumberFormat`
 * and issuance is idempotent, so bulk-issue is safe to re-click.
 */

interface Props {
  cycle: AdmissionCycle;
}

const AdmitCardsTab: React.FC<Props> = ({ cycle }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const canIssue = canWrite('admissions');
  const [rows, setRows] = useState<AdmissionApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 250);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busyBulk, setBusyBulk] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  // Row ⋮ menu — one shared menu, anchored to whichever row opened it.
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; app: AdmissionApplication } | null>(null);

  const { pathways } = cycle;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pull every AssessmentScheduled app in the cycle (bounded set).
      const res = await admissionApplicationService.list({
        cycleId: cycle._id,
        status: 'AssessmentScheduled',
        classId: classFilter || undefined,
        limit: 500,
        page: 1,
      });
      // Only classes whose pathway issues cards; the others never get one.
      setRows(res.items.filter(a => pathwayFor({ pathways }, a.classId).stages?.admitCardEnabled));
      setSelected(new Set());
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load admit-card queue', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [cycle._id, pathways, classFilter, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Client-side search filter — cheap, since the server call is already
  // scoped by cycle+status+class and capped at 500. Matches on applicant
  // name, father's name, application number, and (for issued) roll number.
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => applicantMatches(r, q) || !!r.admitCard?.rollNumber?.toLowerCase().includes(q));
  }, [rows, search]);

  const { pending, issued } = useMemo(() => {
    const p: AdmissionApplication[] = [];
    const i: AdmissionApplication[] = [];
    for (const r of visibleRows) {
      if (r.admitCard?.rollNumber) i.push(r); else p.push(r);
    }
    return { pending: p, issued: i };
  }, [visibleRows]);

  const cycleClasses = useCycleClassOptions(cycle);
  const classOptions = useMemo(
    () => cycleClasses.filter(c => pathwayFor(cycle, c._id).stages?.admitCardEnabled),
    [cycleClasses, cycle],
  );
  // "Issue all" covers the whole class filter, whatever the search shows.
  const allPending = useMemo(() => rows.filter(r => !r.admitCard?.rollNumber).length, [rows]);
  const [confirmIssue, setConfirmIssue] = useState<'all' | 'selected' | null>(null);
  const issueCount = confirmIssue === 'all' ? allPending : selected.size;
  const issueScope = classFilter ? fmtClassName(classOptions.find(c => c._id === classFilter)?.name) : 'All classes';

  const togglePick = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllPending = () => {
    if (selected.size === pending.length && pending.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pending.map(p => p._id)));
    }
  };

  const bulkIssueSelected = async () => {
    if (selected.size === 0) return;
    setBusyBulk(true);
    try {
      // Per-row loop here so a single failure doesn't rollback the batch —
      // backend's bulk endpoint is cycle-wide; this preserves "selected set"
      // semantics.
      const ids = Array.from(selected);
      let okCount = 0;
      for (const id of ids) {
        try {
          await admissionApplicationService.issueAdmitCard(id);
          okCount += 1;
        } catch { /* reported in aggregate below */ }
      }
      enqueueSnackbar(`${okCount}/${ids.length} admit cards issued`, {
        variant: okCount === ids.length ? 'success' : okCount > 0 ? 'warning' : 'error',
      });
      await load();
    } finally {
      setBusyBulk(false);
    }
  };

  const bulkIssueAll = async () => {
    setBusyBulk(true);
    try {
      const out = await admissionApplicationService.bulkIssueAdmitCards(cycle._id, {
        classId: classFilter || undefined,
      });
      enqueueSnackbar(
        `${out.issued.length} issued${out.skipped.length ? `, ${out.skipped.length} skipped` : ''}`,
        { variant: 'success' },
      );
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Bulk issue failed', { variant: 'error' });
    } finally {
      setBusyBulk(false);
    }
  };

  const issueOne = async (app: AdmissionApplication) => {
    setBusyIds(prev => new Set(prev).add(app._id));
    try {
      const out = await admissionApplicationService.issueAdmitCard(app._id);
      enqueueSnackbar(`Admit card ${out.rollNumber} issued`, { variant: 'success' });
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to issue', { variant: 'error' });
    } finally {
      setBusyIds(prev => {
        const next = new Set(prev);
        next.delete(app._id);
        return next;
      });
    }
  };

  const { branding } = useSchoolBranding();
  const { generate: generatePdf, loading: pdfLoading } = usePdfExport();
  // Separate flag for bulk so single-card downloads don't show the modal.
  const [bulkPdfLabel, setBulkPdfLabel] = useState<string | null>(null);

  const downloadAll = async () => {
    if (issued.length === 0) return;
    setBulkPdfLabel(`Rendering ${issued.length} admit cards`);
    try {
      await generatePdf(
        async () => <AdmitCardBulkDocument
          apps={issued}
          cycle={cycle}
          branding={branding}
          photos={await fetchPhotoDataUrls(issued.map(photoFilenameOf))}
          meta={{
            title: 'Admit Cards',
            subtitle: cycle.name,
            generatedAt: new Date().toISOString(),
          }}
        />,
        `admit-cards-${cycle.name.replace(/\s+/g, '-').toLowerCase()}.pdf`,
      );
    } finally {
      setBulkPdfLabel(null);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  if (!(cycle.pathways || []).some(p => p.stages?.admitCardEnabled)) {
    return (
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Alert severity="info" variant="outlined">
          Admit cards are off for every class. Turn them on under <b>Settings → Selection process → Stages</b>.
        </Alert>
      </Box>
    );
  }

  const searching = !!search.trim();
  const allPendingPicked = pending.length > 0 && selected.size === pending.length;

  const actionsCell = (app: AdmissionApplication, lead?: React.ReactNode) => (
    <RowActionsCell app={app} lead={lead} onMenu={(anchor) => setRowMenu({ anchor, app })} />
  );

  return (
    <Box>
      <TabToolbar
        summary={loading && rows.length === 0 ? 'Loading…' : `${pending.length} to issue · ${issued.length} issued`}
        onRefresh={load}
        refreshing={loading}
        actions={
          <>
            {allPending > 0 && canIssue && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlaylistAddCheck fontSize="small" />}
                onClick={() => setConfirmIssue('all')}
                disabled={busyBulk}
                sx={{ textTransform: 'none' }}
              >
                {busyBulk ? 'Issuing…' : `Issue all (${allPending})`}
              </Button>
            )}
            {issued.length > 0 && (
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<BusyIcon busy={!!bulkPdfLabel} size={14}><Download fontSize="small" /></BusyIcon>}
                onClick={downloadAll}
                disabled={pdfLoading}
                sx={{ textTransform: 'none' }}
              >
                Download all ({issued.length})
              </Button>
            )}
          </>
        }
      >
        <TextField
          size="small"
          placeholder="Search by name, application no., or roll #…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ flex: 1, minWidth: 240, maxWidth: 420 }}
        />
        {classOptions.length > 0 && (
          <SearchableSelect
            label="Class"
            value={classFilter}
            onChange={setClassFilter}
            options={[{ value: '', label: 'All' }, ...classOptions.map(c => ({ value: c._id, label: fmtClassName(c.name) }))]}
            placeholder="All classes"
            minWidth={180}
          />
        )}
      </TabToolbar>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ position: 'relative' }}>
        {loading && rows.length > 0 && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1 }} />
        )}
        <Box sx={{ px: 2.5, py: 2.5 }}>
          {loading && rows.length === 0 ? (
            <TableCard>
              <Table size="small">
                <TableHead>
                  <HeadRow cells={['Applicant', 'Class', 'Application no.', '']} />
                </TableHead>
                <TableBody>
                  <TableRowSkeletons columns={4} rows={5} />
                </TableBody>
              </Table>
            </TableCard>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<BadgeIcon />}
              title="No applicants in assessment stage"
              description="Admit cards can only be issued for applications in Assessment Scheduled. Move applications to this status from the Pipeline or Applications tab."
              dense
            />
          ) : visibleRows.length === 0 ? (
            <EmptyState
              icon={<Search />}
              title={`No match for "${search}"`}
              description="Try a different name, application number, or roll number."
              dense
              action={{ label: 'Clear search', onClick: () => setSearchInput('') }}
            />
          ) : (
            <Stack spacing={3.5} sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms ease' }}>
              {/* Ready to issue */}
              {(pending.length > 0 || !searching) && (
                <Box>
                  <GroupLabel
                    count={pending.length}
                    action={selected.size > 0 && canIssue && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary">{selected.size} selected</Typography>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          onClick={() => setConfirmIssue('selected')}
                          disabled={busyBulk}
                          startIcon={<PlaylistAddCheck fontSize="small" />}
                          sx={{ textTransform: 'none' }}
                        >
                          {busyBulk ? 'Issuing…' : 'Issue admit cards'}
                        </Button>
                      </Stack>
                    )}
                  >
                    Ready to issue
                  </GroupLabel>
                  {pending.length === 0 ? (
                    <EmptyState
                      icon={<CheckCircleOutline />}
                      title="Everyone has an admit card"
                      description="Every applicant in Assessment Scheduled has been issued a roll number."
                      dense
                    />
                  ) : (
                    <TableCard>
                      <Table size="small">
                        <TableHead>
                          <HeadRow
                            cells={['Applicant', 'Class', 'Application no.', '']}
                            lead={
                              <Checkbox
                                size="small"
                                checked={allPendingPicked}
                                indeterminate={selected.size > 0 && !allPendingPicked}
                                onChange={toggleAllPending}
                                inputProps={{ 'aria-label': 'Select all ready to issue' }}
                              />
                            }
                          />
                        </TableHead>
                        <TableBody>
                          {pending.map(app => (
                            <TableRow key={app._id} hover selected={selected.has(app._id)} sx={clickableRowSx} onClick={() => setOpenId(app._id)}>
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  size="small"
                                  checked={selected.has(app._id)}
                                  onChange={() => togglePick(app._id)}
                                  inputProps={{ 'aria-label': `Select ${app.name}` }}
                                />
                              </TableCell>
                              <ApplicantCells app={app} />
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" fontFamily="monospace" noWrap>{app.applicationNumber}</Typography>
                              </TableCell>
                              {actionsCell(app, canIssue && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => issueOne(app)}
                                  disabled={busyIds.has(app._id)}
                                  sx={{ textTransform: 'none', mr: 0.5 }}
                                >
                                  {busyIds.has(app._id) ? 'Issuing…' : 'Issue'}
                                </Button>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCard>
                  )}
                </Box>
              )}

              {/* Issued */}
              {(issued.length > 0 || !searching) && (
                <Box>
                  <GroupLabel count={issued.length}>Issued</GroupLabel>
                  {issued.length === 0 ? (
                    <EmptyState
                      icon={<BadgeIcon />}
                      title="No admit cards issued yet"
                      description="Issue admit cards above to assign roll numbers. Issued cards appear here, ready to print."
                      dense
                    />
                  ) : (
                    <TableCard>
                      <Table size="small">
                        <TableHead>
                          <HeadRow cells={['Roll #', 'Applicant', 'Class', 'Issued', '']} />
                        </TableHead>
                        <TableBody>
                          {issued.map(app => (
                            <TableRow key={app._id} hover sx={clickableRowSx} onClick={() => setOpenId(app._id)}>
                              <TableCell>
                                <Box
                                  component="span"
                                  sx={{
                                    px: 0.9, py: 0.3, borderRadius: 1.5,
                                    fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600,
                                    color: 'text.primary', whiteSpace: 'nowrap',
                                    bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                                  }}
                                >
                                  {app.admitCard?.rollNumber}
                                </Box>
                              </TableCell>
                              <ApplicantCells app={app} />
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                  {app.admitCard?.issuedAt ? dateTimeUtils.formatDateForDisplay(app.admitCard.issuedAt) : '—'}
                                </Typography>
                              </TableCell>
                              {actionsCell(app)}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCard>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <ApplicationActionsMenu
        app={rowMenu?.app ?? null}
        cycle={cycle}
        anchorEl={rowMenu?.anchor ?? null}
        onClose={() => setRowMenu(null)}
        onMutated={load}
      />

      {openId && (
        <CandidateDrawer
          applicationId={openId}
          cycle={cycle}
          onClose={() => setOpenId(null)}
          onMutated={load}
        />
      )}

      <PdfRenderingDialog
        open={!!bulkPdfLabel}
        label={bulkPdfLabel || 'Preparing PDF'}
        hint="Keep this tab open until the download starts."
      />
      <ConfirmationDialog
        open={!!confirmIssue}
        onClose={() => setConfirmIssue(null)}
        onConfirm={() => { const kind = confirmIssue; setConfirmIssue(null); if (kind === 'all') bulkIssueAll(); else bulkIssueSelected(); }}
        title={`Issue ${issueCount} admit card${issueCount === 1 ? '' : 's'}?`}
        message="Each applicant gets the next roll number in this cycle. Roll numbers are permanent once issued — they can't be taken back or reused."
        confirmText={`Issue ${issueCount}`}
        confirmColor="primary"
      >
        <ConfirmFacts items={[
          { label: 'Applicants', value: issueCount },
          { label: 'Class', value: confirmIssue === 'all' ? issueScope : 'Selected rows' },
        ]} />
      </ConfirmationDialog>
    </Box>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Header row; `lead` fills an optional checkbox column. */
const HeadRow: React.FC<{ cells: string[]; lead?: React.ReactNode }> = ({ cells, lead }) => (
  <TableRow>
    {lead && <TableCell padding="checkbox" sx={tableHeadCellSx}>{lead}</TableCell>}
    {cells.map((c, i) => (
      <TableCell key={c || i} align={c ? 'left' : 'right'} sx={tableHeadCellSx}>{c || ' '}</TableCell>
    ))}
  </TableRow>
);

/** Applicant (name bold, father muted) + class — shared by both groups. */
const ApplicantCells: React.FC<{ app: AdmissionApplication }> = ({ app }) => (
  <>
    <TableCell><ApplicantCell app={app} /></TableCell>
    <TableCell>
      <Typography variant="body2">
        {typeof app.classId === 'object' ? fmtClassName(app.classId?.name) || '—' : '—'}
      </Typography>
    </TableCell>
  </>
);

export default AdmitCardsTab;
